/**
 * Il registro delle operazioni, letto da chi lo riguarda.
 *
 * Due viste, non una con un filtro:
 *
 *   «Solo le mie»      → `GET /api/attivita/mie`, per chiunque sia collegato.
 *   «Tutto l'ufficio»  → `GET /api/attivita`, solo per gli amministratori.
 *
 * ⚠️ Le due viste devono essere **distinguibili a colpo d'occhio**, e non da
 * un pulsante premuto in cima. Chi guarda un elenco di operazioni deve sapere
 * in ogni momento se sta vedendo sé stesso o tutti: scambiare le due cose
 * significa credere che un collega non abbia fatto niente perché si stava
 * guardando il proprio elenco. Per questo la differenza è portata da tre
 * segnali insieme — l'insegna colorata sopra la tabella, la colonna «Chi» che
 * esiste solo nella vista d'ufficio, e l'indirizzo della pagina — e non dal
 * solo colore, che da solo non basta mai.
 *
 * ⚠️ `avvenutoIl` arriva in UTC senza dirlo: passa da `istante()`, che aggiunge
 * la `Z` prima di convertire. Un `new Date()` diretto d'estate sbaglia di due
 * ore, cioè abbastanza da spostare un'operazione al giorno prima.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { attivita as apiAttivita, utenti as apiUtenti } from '../api/scadenziario.ts';
import type { Elenco, Utente, VoceAttivita } from '../api/tipi-dati.ts';
import { useAutenticazione } from '../auth/contesto.tsx';
import { Icona } from '../componenti/Icona.tsx';
import {
  descriviIntervallo,
  etichettaAzione,
  numeroPagine,
  separaAutore,
  tonoAzione,
} from '../componenti/attivita.ts';
import { istante } from '../componenti/formato.ts';
import { puoAmministrare } from '../auth/ruoli.ts';

/**
 * Cinquanta righe per pagina.
 *
 * Un registro si legge scorrendo, non cercando: pagine corte costringono a
 * cliccare in continuazione, pagine lunghissime fanno aspettare per righe che
 * quasi sempre non si guardano. Cinquanta stanno in due schermate di un
 * monitor da ufficio.
 */
const PER_PAGINA = 50;

export function Attivita() {
  const { utente } = useAutenticazione();
  const sonoAmministratore = puoAmministrare(utente?.ruolo);

  // I filtri e la vista vivono nell'indirizzo, come nell'elenco delle
  // scadenze: una ricerca si può mandare a un collega, e «indietro» torna a
  // quella di prima invece di uscire dalla pagina.
  const [parametri, setParametri] = useSearchParams();

  /*
   * ⚠️ La vista d'ufficio si apre solo se **si è amministratori**, non solo se
   * lo dice l'indirizzo. Chi scrive `?vista=tutti` a mano da operatore
   * riceverebbe un 403 dal backend — la guardia vera è là — ma qui si evita di
   * mostrargli una schermata rotta per una cosa che non poteva ottenere.
   */
  const tutti = sonoAmministratore && parametri.get('vista') === 'tutti';

  const filtroUtente = tutti ? parametri.get('utente') ?? '' : '';
  const dal = tutti ? parametri.get('dal') ?? '' : '';
  const al = tutti ? parametri.get('al') ?? '' : '';
  const pagina = Math.max(1, Number(parametri.get('pagina') ?? '1') || 1);

  const conFiltri = [filtroUtente, dal, al].some((v) => v !== '');

  const [dati, setDati] = useState<Elenco<VoceAttivita> | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCaricamento, setInCaricamento] = useState(true);

  /** Le persone del filtro. Serve solo nella vista d'ufficio, e solo lì si chiede. */
  const [persone, setPersone] = useState<Utente[]>([]);

  useEffect(() => {
    let annullato = false;
    setInCaricamento(true);

    const richiesta = tutti
      ? apiAttivita.tutte({
        pagina,
        perPagina: PER_PAGINA,
        ...(filtroUtente !== '' ? { utenteId: Number(filtroUtente) } : {}),
        ...(dal !== '' ? { dal } : {}),
        ...(al !== '' ? { al } : {}),
      })
      : apiAttivita.mie({ pagina, perPagina: PER_PAGINA });

    richiesta
      .then((r) => { if (!annullato) { setDati(r); setErrore(null); } })
      .catch((e: unknown) => {
        if (!annullato) {
          setDati(null);
          setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
        }
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [tutti, pagina, filtroUtente, dal, al]);

  useEffect(() => {
    if (!tutti) return;

    let annullato = false;
    apiUtenti
      .elenca(true)
      .then((r) => { if (!annullato) setPersone(r.elementi); })
      // Silenzioso di proposito: senza l'elenco delle persone si perde una
      // tendina di filtro, non il registro. Svuotare la pagina per questo
      // sarebbe sproporzionato.
      .catch((e: unknown) => { console.error('Elenco delle persone non caricato', e); });

    return () => { annullato = true; };
  }, [tutti]);

  /** Cambia un parametro e riparte da pagina 1: i filtri di prima valevano su altre righe. */
  function aggiorna(chiave: string, valore: string) {
    const nuovi = new URLSearchParams(parametri);
    if (valore === '') nuovi.delete(chiave);
    else nuovi.set(chiave, valore);
    nuovi.delete('pagina');
    setParametri(nuovi, { replace: true });
  }

  function vaiAPagina(numero: number) {
    const nuovi = new URLSearchParams(parametri);
    if (numero <= 1) nuovi.delete('pagina');
    else nuovi.set('pagina', String(numero));
    setParametri(nuovi);
  }

  /**
   * Il passaggio da una vista all'altra azzera tutto il resto.
   *
   * I filtri per persona e per data appartengono alla vista d'ufficio:
   * portarseli dietro nel proprio elenco significherebbe tenere
   * nell'indirizzo un `?utente=3` che non filtra più niente, e ritrovarselo
   * attivo tornando indietro.
   */
  function cambiaVista(aTutti: boolean) {
    setParametri(aTutti ? { vista: 'tutti' } : {}, { replace: true });
  }

  const pagine = numeroPagine(dati?.totale ?? 0, PER_PAGINA);

  return (
    <>
      <div className="titolo-pagina">
        <h1>Attività</h1>
        {dati !== null && dati.elementi.length > 0 && (
          <span className="conteggio">
            {descriviIntervallo(pagina, PER_PAGINA, dati.elementi.length, dati.totale)}
          </span>
        )}
      </div>

      {/*
        La scelta della vista esiste solo per gli amministratori: per tutti gli
        altri non c'è nessuna scelta da fare, e due pulsanti di cui uno sempre
        premuto sarebbero solo un ingombro che fa domandare cosa manchi.
      */}
      {sonoAmministratore && (
        <div className="scelta-vista" role="group" aria-label="Di chi vedere le attività">
          <button
            type="button"
            className="bottone"
            aria-pressed={!tutti}
            onClick={() => cambiaVista(false)}
          >
            <IconaPersona /> Solo le mie
          </button>
          <button
            type="button"
            className="bottone"
            aria-pressed={tutti}
            onClick={() => cambiaVista(true)}
          >
            <IconaUfficio /> Tutto l'ufficio
          </button>
        </div>
      )}

      {/*
        L'insegna: il segnale che si vede senza cercarlo, e che resta anche
        scorrendo con la tabella davanti agli occhi. Il colore non è l'unico
        segnale — cambiano anche l'icona e la frase — perché il colore da solo
        non arriva a chi non lo distingue.
      */}
      <div className={`insegna-vista ${tutti ? 'insegna-ufficio' : 'insegna-mie'}`}>
        {tutti ? <IconaUfficio /> : <IconaPersona />}
        <p>
          {tutti ? (
            <>
              <strong>Stai vedendo le attività di tutto l'ufficio.</strong> Ogni riga porta il
              nome di chi l'ha fatta; le tue sono segnate «sei tu». È la vista riservata agli
              amministratori.
            </>
          ) : (
            <>
              <strong>Stai vedendo solo quello che hai fatto tu</strong>
              {utente !== null && `, ${utente.nome}`}. Le operazioni dei colleghi non compaiono
              in questo elenco{sonoAmministratore ? ': per quelle usa «Tutto l\'ufficio».' : '.'}
            </>
          )}
        </p>
      </div>

      {tutti && (
        <div className="filtri">
          <div className="campo">
            <label htmlFor="a-utente">Persona</label>
            <select
              id="a-utente"
              value={filtroUtente}
              onChange={(e) => aggiorna('utente', e.target.value)}
            >
              <option value="">Tutte le persone</option>
              {persone.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.nome}{p.attivo ? '' : ' (senza accesso)'}
                </option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="a-dal">Dal</label>
            <input id="a-dal" type="date" value={dal} onChange={(e) => aggiorna('dal', e.target.value)} />
          </div>

          <div className="campo">
            <label htmlFor="a-al">Al</label>
            <input id="a-al" type="date" value={al} onChange={(e) => aggiorna('al', e.target.value)} />
          </div>

          {conFiltri && (
            <button
              type="button"
              className="bottone-piatto"
              onClick={() => setParametri({ vista: 'tutti' }, { replace: true })}
            >
              Azzera filtri
            </button>
          )}
        </div>
      )}

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <div className={`pannello registro ${tutti ? 'registro-ufficio' : 'registro-mie'}`}>
        {inCaricamento ? (
          <p className="stato-caricamento">Caricamento…</p>
        ) : dati === null || dati.elementi.length === 0 ? (
          <StatoVuoto tutti={tutti} conFiltri={conFiltri} />
        ) : (
          <div className="tabella-contenitore">
            <table>
              <caption className="solo-lettori-schermo">
                {tutti
                  ? "Registro delle operazioni di tutto l'ufficio, dalla più recente"
                  : 'Le tue operazioni, dalla più recente'}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Quando</th>
                  {/* La colonna esiste solo quando c'è più di una persona da
                      nominare: nella vista personale sarebbe il proprio nome
                      ripetuto cinquanta volte, e una colonna che dice sempre
                      la stessa cosa smette di essere letta. */}
                  {tutti && <th scope="col">Chi</th>}
                  <th scope="col">Operazione</th>
                  <th scope="col" className="col-espandi">Cosa è successo</th>
                  {/* L'indirizzo IP solo nella vista d'ufficio, e per lo stesso
                      motivo della colonna «Chi»: nella propria pagina sarebbe
                      il proprio indirizzo ripetuto a ogni riga. Serve a chi
                      guarda le operazioni altrui — è lì che distingue «era in
                      ufficio» da «era altrove». */}
                  {tutti && <th scope="col">IP</th>}
                </tr>
              </thead>
              <tbody>
                {dati.elementi.map((v) => (
                  <Riga key={v.id} voce={v} conAutore={tutti} ioSono={utente?.id ?? null} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagine > 1 && (
        <div className="paginazione">
          <button
            type="button"
            className="bottone"
            disabled={pagina <= 1}
            onClick={() => vaiAPagina(pagina - 1)}
          >
            ← Più recenti
          </button>
          <span className="tenue">Pagina {pagina} di {pagine}</span>
          <button
            type="button"
            className="bottone"
            disabled={pagina >= pagine}
            onClick={() => vaiAPagina(pagina + 1)}
          >
            Più vecchie →
          </button>
        </div>
      )}
    </>
  );
}

function Riga({
  voce, conAutore, ioSono,
}: {
  voce: VoceAttivita;
  conAutore: boolean;
  ioSono: number | null;
}) {
  const autore = separaAutore(voce.autore.etichetta);
  const tono = tonoAzione(voce.azione);
  const etichetta = etichettaAzione(voce.azione);
  const sonoIo = voce.autore.id !== null && voce.autore.id === ioSono;

  return (
    <tr className={tono === 'fallita' ? 'riga-scaduta' : ''}>
      <td className="data">{istante(voce.avvenutoIl)}</td>

      {conAutore && (
        <td>
          <span className="soggetto-nome">{autore.nome}</span>
          {sonoIo && <span className="tenue"> · sei tu</span>}
          {/* L'indirizzo distingue due colleghi che si chiamano allo stesso
              modo, e non serve a nient'altro: sta sotto, in piccolo. */}
          {autore.email !== null && <span className="autore-email">{autore.email}</span>}
        </td>
      )}

      <td>
        {etichetta !== '' && (
          <span className={`segno-azione azione-${tono}`}>{etichetta}</span>
        )}
      </td>

      {/* Già in italiano, dal backend: qui non si ricompone niente. */}
      <td className="col-espandi esito">{voce.descrizione}</td>

      {/* ⚠️ La colonna «Voce» (`entita` + `entitaId`, es. «scadenze nº 42») è
          stata tolta il 7 agosto su richiesta di Enrico, perché in una tabella
          già densa diceva poco a chi legge.
          Il prezzo, scritto qui perché non si scopra indagando: la descrizione
          del backend è generica — «Ha segnato completata una scadenza» — quindi
          *quale* scadenza non è più ricostruibile da questa pagina. I due campi
          continuano ad arrivare nella risposta: rimetterla è una riga. */}

      {conAutore && (
        <td className="indirizzo-ip">
          {voce.indirizzoIp ?? <span className="tenue">—</span>}
        </td>
      )}
    </tr>
  );
}

/**
 * La pagina senza righe.
 *
 * ⚠️ È **il primo stato in cui questa pagina verrà vista**: il registro è stato
 * svuotato il 7 agosto 2026, quindi al primo caricamento non c'è niente da
 * mostrare. Deve dire che è normale. Una pagina vuota e muta, su una
 * funzionalità appena arrivata, si legge come «non è ancora finita» o «si è
 * rotto qualcosa» — e chi lo pensa non ci torna più.
 */
function StatoVuoto({ tutti, conFiltri }: { tutti: boolean; conFiltri: boolean }) {
  if (conFiltri) {
    return (
      <p className="stato-vuoto">
        Nessuna operazione corrisponde ai filtri impostati. Prova ad allargare
        l'intervallo di date o a scegliere «Tutte le persone».
      </p>
    );
  }

  return (
    <p className="stato-vuoto">
      {tutti
        ? 'Il registro è vuoto: nessuna operazione è ancora stata scritta.'
        : 'Non hai ancora fatto niente che il registro tenga da conto.'}
      {' '}
      Non è un errore e non manca niente: le righe compaiono qui da sole man mano che
      si lavora — quando si crea, si modifica, si completa, si riapre o si annulla
      qualcosa. Torna a dare un'occhiata dopo la prima scadenza segnata come completata.
    </p>
  );
}

/* --- Icone ---------------------------------------------------------------- */

function IconaPersona() {
  return (
    <Icona lato={15}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20c0-3.5 3.3-5.4 7.2-5.4s7.2 1.9 7.2 5.4" />
    </Icona>
  );
}

/** Tre persone invece di una: la differenza si legge anche senza il colore. */
function IconaUfficio() {
  return (
    <Icona lato={15}>
      <circle cx="9.6" cy="8.4" r="3.2" />
      <path d="M3.2 19.4c0-3.1 2.9-4.8 6.4-4.8s6.4 1.7 6.4 4.8" />
      <path d="M16.4 5.6a3 3 0 0 1 0 5.7" />
      <path d="M18 14.9c2 .6 3.2 1.9 3.2 4" />
    </Icona>
  );
}
